import _ from 'lodash'
import {
  type V1SelectorElement,
  type V1SelectorComponent,
  SelectorComponentSelectType
} from 'mnvm-mypage-app-ts-if'
import { useReducer } from 'react'
import {
  type OnSelectPayload,
  type OnSelectAllPayload,
  type DefaultValuePayload
} from './fn'
import { type ScrollToIndexOptions } from 'react-viewport-list'
import { EJobPostingAdvancedSearchType } from '@/hooks/useJobPostingAdvancedSearchConditionBackup'

export interface FormElementsSelectorAction<T> {
  type: string
  payload?: T
}

export const FETCHING_DATA_SELECTOR_ELEMENTS = 'FETCHING_DATA_SELECTOR_ELEMENTS'
export const ADD_DEFAULT_VALUES_SELECTOR_ELEMENTS =
  'ADD_DEFAULT_VALUES_SELECTOR_ELEMENTS'
export const ADD_SELECTOR_ELEMENTS = 'ADD_SELECTOR_ELEMENTS'
export const ON_SELECT = 'ON_SELECT'
export const ON_SELECT_ALL = 'ON_SELECT_ALL'
export const RESET = 'RESET'
export const ON_RESET_SELECT = 'ON_RESET_SELECT'
export const ON_BACK = 'ON_BACK'
export const ADD_SCROLL_POSITION = 'ADD_SCROLL_POSITION'
export const ON_REMOVE_LAST_SCROLL_POSITION = 'ON_REMOVE_LAST_SCROLL_POSITION'

export interface FormElementsSelectorState {
  isLoading: boolean
  items: V1SelectorComponent[]
  allElements: V1SelectorElement[]
  values: string[]
  targetElements: V1SelectorElement[]
  municipalityMap: Record<string, string[]>
  scrollPositions: ScrollToIndexOptions[]
  isScrollToLastPosition: boolean
}

export const initFormElementsSelectorState: {
  state: FormElementsSelectorState
  dispatch: React.Dispatch<FormElementsSelectorAction<any>>
} = {
  state: {
    isLoading: false,
    values: [],
    items: [],
    targetElements: [],
    allElements: [],
    municipalityMap: {},
    scrollPositions: [],
    isScrollToLastPosition: false
  },
  dispatch: (a: FormElementsSelectorAction<any>) => {}
}

const reducer = (
  state: FormElementsSelectorState,
  action: FormElementsSelectorAction<any>
): FormElementsSelectorState => {
  switch (action.type) {
    case FETCHING_DATA_SELECTOR_ELEMENTS: {
      if (action.payload !== undefined) {
        return {
          ...state,
          isLoading: !state.isLoading,
          targetElements: [
            ...state.targetElements,
            action.payload as V1SelectorElement
          ]
        }
      }
      return { ...state, isLoading: !state.isLoading }
    }
    case ADD_DEFAULT_VALUES_SELECTOR_ELEMENTS: {
      const { values, municipalityMap }: DefaultValuePayload = action.payload
      return {
        ...state,
        values,
        municipalityMap
      }
    }
    case RESET: {
      return { ...initFormElementsSelectorState.state }
    }
    case ON_RESET_SELECT: {
      return { ...state, values: [], municipalityMap: {}, scrollPositions: [] }
    }
    case ON_BACK: {
      return {
        ...state,
        targetElements: _.dropRight(state.targetElements),
        items: _.dropRight(state.items),
        isScrollToLastPosition: !state.isScrollToLastPosition
      }
    }

    case ADD_SCROLL_POSITION: {
      return {
        ...state,
        scrollPositions: [
          ...state.scrollPositions,
          action.payload as ScrollToIndexOptions
        ]
      }
    }

    case ON_REMOVE_LAST_SCROLL_POSITION: {
      return {
        ...state,
        scrollPositions: _.dropRight(state.scrollPositions)
      }
    }

    case ADD_SELECTOR_ELEMENTS: {
      if (action.payload === undefined) return { ...state }
      const { elements, selectType } = action.payload as V1SelectorComponent
      switch (selectType) {
        case SelectorComponentSelectType.Radio:
        case SelectorComponentSelectType.Checkbox:
          return {
            ...state,
            items: [...state.items, action.payload as V1SelectorComponent],
            allElements: _.uniqBy(
              [...state.allElements, ...(elements ?? [])],
              'id'
            )
          }
      }
      return {
        ...state,
        items: [...state.items, action.payload as V1SelectorComponent]
      }
    }

    case ON_SELECT: {
      const item = _.last(state.items)
      const { itemSelected, backUpData }: OnSelectPayload = action.payload
      if (itemSelected === undefined || item === undefined) return { ...state }
      const isSelected =
        _.findIndex(state.values, (v) => v === itemSelected.data) > -1

      const currentSearchType = backUpData?.searchType

      let tempMunicipalityMap = { ...backUpData?.municipalityMap }

      const parentMunicipalityKey = _.first(state.targetElements)?.data ?? ''
      if (isSelected) {
        tempMunicipalityMap = _.omit(backUpData?.municipalityMap ?? {}, [
          parentMunicipalityKey
        ])
      }

      switch (item.selectType) {
        case SelectorComponentSelectType.Checkbox:
          return {
            ...state,
            municipalityMap:
              currentSearchType === EJobPostingAdvancedSearchType.MUNICIPALITY
                ? tempMunicipalityMap
                : {},
            values: isSelected
              ? [..._.filter(state.values, (r) => r !== itemSelected.data)]
              : [...state.values, itemSelected.data ?? '']
          }
        case SelectorComponentSelectType.Radio:
          return {
            ...state,
            values: isSelected ? [] : [itemSelected.data ?? '']
          }
        default:
          break
      }
      return {
        ...state
      }
    }
    case ON_SELECT_ALL: {
      const { items } = state
      const { checked, backUpData }: OnSelectAllPayload = action.payload

      const currentSearchType = backUpData?.searchType

      if (_.isBoolean(checked)) {
        const options = (_.last(items)?.elements ?? []).filter(
          (e) => e.id !== undefined
        )

        let tempMunicipalityMap =
          currentSearchType === EJobPostingAdvancedSearchType.MUNICIPALITY
            ? { ...backUpData?.municipalityMap }
            : {}

        const parentMunicipalityKey = _.first(state.targetElements)?.data ?? ''

        if (currentSearchType === EJobPostingAdvancedSearchType.MUNICIPALITY) {
          tempMunicipalityMap = {
            ...backUpData?.municipalityMap,
            [parentMunicipalityKey]: options.map((e) => e.data ?? '')
          }
        }

        // Check All is true
        if (checked) {
          const stateReturn = {
            ...state,
            municipalityMap: tempMunicipalityMap,
            values: _.unionWith(
              [...options.map((o) => o.data ?? '')],
              [...state.values],
              _.isEqual
            )
          }
          return stateReturn
        }
        // Check All is false
        if (currentSearchType === EJobPostingAdvancedSearchType.MUNICIPALITY) {
          tempMunicipalityMap = _.omit(backUpData?.municipalityMap ?? {}, [
            parentMunicipalityKey
          ])
        }

        const stateReturn = {
          ...state,
          municipalityMap: tempMunicipalityMap,
          values: [
            ...state.values.filter(
              (value) => _.findIndex(options, (v) => v.data === value) === -1
            )
          ]
        }
        return stateReturn
      }
      return { ...state }
    }
  }
  return state
}

export const useFormElementsSelector = (): [
  FormElementsSelectorState,
  React.Dispatch<FormElementsSelectorAction<any>>
] => {
  return useReducer(reducer, initFormElementsSelectorState.state)
}
